/* eslint-disable max-len */
import React, { useState, useRef, useEffect } from 'react';

// Redux
import { useSelector } from 'react-redux';

// Draggable Scroll
import { useDraggable } from 'react-use-draggable-scroll';

// Material
import Table from '@material-ui/core/Table';
import TableContainer from '@material-ui/core/TableContainer';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Link from '@material-ui/core/Link';
import IconButton from '@material-ui/core/IconButton';
import TableSortLabel from '@material-ui/core/TableSortLabel';

// Material Icons
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';

// Types
import { ApplicationState } from 'store';
import { ImportationsReportInterface } from 'store/ducks/company/types';

// Helpers
import { returnDateAndTime } from 'helpers/date';
import { Order, stableSort, getComparator } from 'helpers/tableSortLabels';

// Styles
import useStyles from './DetailedImportationsReport.styles';

interface DetailedImportationsReportInterface {
  setTableReference: (arg: any) => void;
  importationList?: ImportationsReportInterface[];
}

type Props = DetailedImportationsReportInterface;

const DetailedImportationsReport: React.FC<Props> = ({
  setTableReference,
  importationList,
}) => {
  const classes = useStyles();
  const parentRef: any = useRef();
  const { events } = useDraggable(parentRef);

  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState('name');
  const [initialList, setInitialList] = useState<ImportationsReportInterface[]>(
    [],
  );

  const transportOperatorSelected = useSelector(
    (state: ApplicationState) =>
      state.transportOperators.transportOperatorSelected,
  );

  const transportOperatorsList = useSelector(
    (state: ApplicationState) =>
      state.transportOperators.transportOperatorsList,
  );

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string,
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  useEffect(() => {
    if (importationList) setInitialList(importationList);
  }, [importationList]);
  return (
    <TableContainer
      className={classes.tableContainer}
      {...events}
      ref={parentRef}>
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell
              variant='head'
              align='left'
              sortDirection={orderBy === 'fileName' ? order : false}>
              <TableSortLabel
                classes={{
                  root: classes.sortLabel,
                  icon: classes.sortLabel,
                }}
                active
                direction={orderBy === 'fileName' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'fileName')}>
                Nome do Arquivo
                {orderBy === 'fileName' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell
              variant='head'
              align='left'
              sortDirection={orderBy === 'company' ? order : false}>
              <TableSortLabel
                classes={{
                  root: classes.sortLabel,
                  icon: classes.sortLabel,
                }}
                active
                direction={orderBy === 'company' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'company')}>
                Empresa
                {orderBy === 'company' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell
              variant='head'
              align='left'
              sortDirection={orderBy === 'value' ? order : false}>
              <TableSortLabel
                classes={{
                  root: classes.sortLabel,
                  icon: classes.sortLabel,
                }}
                active
                direction={orderBy === 'value' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'value')}>
                Valor
                {orderBy === 'value' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell
              variant='head'
              align='left'
              sortDirection={orderBy === 'nsu' ? order : false}>
              <TableSortLabel
                classes={{
                  root: classes.sortLabel,
                  icon: classes.sortLabel,
                }}
                active
                direction={orderBy === 'nsu' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'nsu')}>
                NSU
                {orderBy === 'nsu' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell
              variant='head'
              align='left'
              sortDirection={orderBy === 'createdAt' ? order : false}>
              <TableSortLabel
                classes={{
                  root: classes.sortLabel,
                  icon: classes.sortLabel,
                }}
                active
                direction={orderBy === 'createdAt' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'createdAt')}>
                Data
                {orderBy === 'createdAt' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            {transportOperatorSelected.type === 1 && (
              <TableCell
                variant='head'
                align='left'
                sortDirection={
                  orderBy === 'transportOperatorId' ? order : false
                }>
                <TableSortLabel
                  classes={{
                    root: classes.sortLabel,
                    icon: classes.sortLabel,
                  }}
                  active
                  direction={orderBy === 'transportOperatorId' ? order : 'asc'}
                  onClick={(event) =>
                    handleRequestSort(event, 'transportOperatorId')
                  }>
                  Operação
                  {orderBy === 'transportOperatorId' ? (
                    <span className={classes.visuallyHidden}>
                      {order === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>
            )}
            <TableCell variant='head' align='left' />
          </TableRow>
        </TableHead>
        <TableBody>
          {initialList.length > 0 &&
            stableSort(initialList, getComparator(order, orderBy)).map(
              (importation) => (
                <TableRow key={importation.nsu}>
                  <TableCell>{importation.fileName}</TableCell>
                  <TableCell>{importation.company}</TableCell>
                  <TableCell>{importation.value}</TableCell>
                  <TableCell>{importation.nsu}</TableCell>
                  <TableCell>
                    {returnDateAndTime(importation.createdAt)}
                  </TableCell>
                  {transportOperatorSelected.type === 1 && (
                    <TableCell>
                      {transportOperatorsList.find(
                        (e) => e.id === importation?.transportOperatorId,
                      )?.name || '---'}
                    </TableCell>
                  )}
                  <TableCell>
                    <Link href={importation.fileUrl}>
                      <IconButton>
                        <ArrowDownwardIcon />
                      </IconButton>
                    </Link>
                  </TableCell>
                </TableRow>
              ),
            )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default DetailedImportationsReport;
export { DetailedImportationsReport };
